import { convertPublicationDate } from '@doda/common/src/logics/JobContent/createFormatDate';
import { RankedJob } from '@doda/common/src/types/jobSearchList/jobContent';
import { removeLF } from '@doda/common/src/utils/convTextUtil';
import {
  Box,
  Button,
  FillTag,
  Income,
  Location,
  OutlineTag,
  Text,
} from '@doda/io-react/common';
import classNames from 'classnames';
import { Favorite } from 'components/Favorite';
import { createRankingSalaryText } from '@doda/common/src/logics/jobSearchList/createRankingSalaryText';
import { MouseEvent, useRef } from 'react';
import { measureBookmarkClick } from '@doda/common/src/logics/jobSearchList/jobRankingAnalytics';
import styles from './index.module.scss';

/**
 * 人気求人ランキングの求人カードProps
 * @param rankedJob 人気求人ランキングの求人データ
 */
interface RankedJobCardProps {
  rankedJob: RankedJob;
}

/**
 * 人気求人ランキングの求人カード
 */
export const RankedJobCard = ({ rankedJob }: RankedJobCardProps) => {
  const isBookmarkClickedRef = useRef(false);

  // 改行コードを取り除く処理
  const modifiedLocation = removeLF(rankedJob.location);

  // 給与情報を給与例または給与情報から作成
  const modifiedSalary = createRankingSalaryText(
    rankedJob.salaryExamples,
    rankedJob.salary
  );

  const handleClickCard = (event: MouseEvent<HTMLAnchorElement>) => {
    event.currentTarget.href = `${rankedJob.url}?sid=JobRanking_link_KyuujinCassette_click`;
  };

  const handleClickBookmark = () => {
    if (isBookmarkClickedRef.current) return;
    isBookmarkClickedRef.current = true;
    measureBookmarkClick(rankedJob.id);
  };

  return (
    <Box
      radius="4"
      boxShadow="black"
      border="gray"
      className={classNames(styles.rankedJobCard, {
        [styles.rankedJobCard__first]: rankedJob.rank === 1,
        [styles.rankedJobCard__second]: rankedJob.rank === 2,
        [styles.rankedJobCard__third]: rankedJob.rank === 3,
      })}
    >
      <div className={styles.rankedJobCard__title}>
        <img
          className={styles.rankedJobCard__rankIcon}
          src={`/assets/icon/jobContent/ranking-${rankedJob.rank}.svg`}
          alt={`${rankedJob.rank}位`}
        />
        <div className={styles.rankedJobCard__titleWrapper}>
          <div className={styles.rankedJobCard__tagWrapper}>
            {rankedJob.isRead && (
              <FillTag
                color="gray"
                className={classNames(
                  styles.rankedJobCard__grayBadge,
                  styles.rankedJobCard__tag
                )}
              >
                閲覧済
              </FillTag>
            )}
            {rankedJob.isNewArrival && (
              <FillTag color="red" className={styles.rankedJobCard__tag}>
                New
              </FillTag>
            )}
            {rankedJob.isNearDeadline && (
              <OutlineTag color="red" className={styles.rankedJobCard__tag}>
                締切間近
              </OutlineTag>
            )}
          </div>
          <a
            href={rankedJob.url}
            onClick={handleClickCard}
            target="_blank"
            rel="noopener"
            className={styles.rankedJobCard__link}
          >
            <Text as="h2" size="20" color="blue" weight="bold">
              {rankedJob.corporateName}
            </Text>
            <Text
              as="h3"
              size="14--articlePC"
              color="black_pc"
              weight="normal"
              className={styles.rankedJobCard__summary}
            >
              {rankedJob.summary}
            </Text>
          </a>
        </div>
      </div>
      <div className={styles.rankedJobCard__detail}>
        <div className={styles.rankedJobCard__leftArea}>
          <span className={styles.rankedJobCard__detailItem}>
            <Location size="XS" color="blue" />
            <Text
              as="span"
              size="14--articlePC"
              className={styles['rankedJobCard__detailItem--text']}
            >
              {modifiedLocation}
            </Text>
          </span>
          <span className={styles.rankedJobCard__detailItem}>
            <Income size="XS" color="blue" />
            <Text
              as="span"
              size="14--articlePC"
              className={styles['rankedJobCard__detailItem--text']}
            >
              {modifiedSalary}
            </Text>
          </span>
        </div>
        <div className={styles.rankedJobCard__rightArea}>
          <Text as="div" size="12" weight="normal" color="gray">
            掲載予定期間：
            {convertPublicationDate(rankedJob.publicationFrom)}〜
            {convertPublicationDate(rankedJob.publicationTo)}
            <br />
            更新日：{convertPublicationDate(rankedJob.updatedDate)}
          </Text>
        </div>
      </div>
      <div className={styles.rankedJobCard__buttons}>
        <Button
          as="a"
          variant="green"
          size="S"
          href={rankedJob.url}
          target="_blank"
          rel="noopener"
          className={styles.rankedJobCard__seeJobDetailsButton}
          onClick={handleClickCard}
        >
          求人詳細を見る
        </Button>
        <Favorite
          size="S"
          width="150px"
          jid={rankedJob.id}
          isBookmarked={rankedJob.isBookmarked}
          isApplied={rankedJob.isApplied}
          requestBody={{ device: 'PC' }}
          bookmarkListUrl="/dcfront/bookmark/bookmarkList"
          onClickFavorite={handleClickBookmark}
        />
      </div>
    </Box>
  );
};
